import { Component, Inject, ChangeDetectionStrategy } from '@angular/core'
import { MatCheckboxModule } from '@angular/material/checkbox'
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { EMPTY, Observable, catchError, finalize, forkJoin, tap } from 'rxjs'

import { ButtonComponent } from '../../../components/button/button.component'
import {
  AssessmentTag,
  AssessmentTagMaster,
} from '../../../services/assessment/assessment.mapping'
import { AssessmentStore } from '../../../stores/assessment.store'

export type EditAssessmentTagsDialogData = {
  assessmentId: string
  tagMasters: AssessmentTagMaster[]
  tags: AssessmentTag[]
}

export type EditAssessmentTagsDialogResult = {
  hasIssuedChanges: boolean
}

@UntilDestroy()
@Component({
  selector: 'app-edit-assessment-tags',
  standalone: true,
  imports: [ButtonComponent, MatCheckboxModule, MatDialogTitle, MatIcon],
  templateUrl: './edit-assessment-tags.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './edit-assessment-tags.component.scss',
})
export class EditAssessmentTagsComponent {
  readonly selectedTagMasterIds: Set<string>
  saving = false
  errorMessage: string | null = null
  private hasIssuedChanges = false

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: EditAssessmentTagsDialogData,
    private readonly dialogRef: MatDialogRef<EditAssessmentTagsComponent>,
    private readonly assessmentStore: AssessmentStore,
  ) {
    this.selectedTagMasterIds = new Set(data.tags.map((tag) => tag.tagMasterId))
  }

  isSelected(tagMasterId: string): boolean {
    return this.selectedTagMasterIds.has(tagMasterId)
  }

  toggleTag(tagMasterId: string, checked: boolean): void {
    if (checked) {
      this.selectedTagMasterIds.add(tagMasterId)
      return
    }

    this.selectedTagMasterIds.delete(tagMasterId)
  }

  save(): void {
    if (this.saving) {
      return
    }

    const assignedTagMasterIds = new Set(
      this.data.tags.map((tag) => tag.tagMasterId),
    )
    const requests: Observable<AssessmentTag[]>[] = []

    for (const tagMaster of this.data.tagMasters) {
      const selected = this.selectedTagMasterIds.has(tagMaster.id)
      const assigned = assignedTagMasterIds.has(tagMaster.id)
      if (selected && !assigned) {
        requests.push(
          this.assessmentStore.addAssessmentTag(
            this.data.assessmentId,
            tagMaster.id,
          ),
        )
      } else if (!selected && assigned) {
        requests.push(
          this.assessmentStore.removeAssessmentTag(
            this.data.assessmentId,
            tagMaster.id,
          ),
        )
      }
    }

    if (requests.length === 0) {
      this.closeDialog()
      return
    }

    this.hasIssuedChanges = true
    this.saving = true
    this.errorMessage = null
    forkJoin(requests)
      .pipe(
        tap(() => this.closeDialog()),
        catchError(() => {
          this.errorMessage =
            '一部のタグのみ更新された可能性があります。再試行するか、閉じて一覧を確認してください。'
          return EMPTY
        }),
        finalize(() => (this.saving = false)),
        untilDestroyed(this),
      )
      .subscribe()
  }

  close(): void {
    if (this.saving) {
      return
    }

    this.closeDialog()
  }

  private closeDialog(): void {
    this.dialogRef.close({ hasIssuedChanges: this.hasIssuedChanges })
  }
}
